// «Ціни» v2 — дані РДН з прогнозом: одна часова шкала (факт + лінії BASE / EXPRO + «як учора»).
//
// Сервер (/market/price-forecast/lines | range | export) уже віддає для кожної доби «заморожені» прогони —
// випущені ДО закриття подачі заявок на цю добу (D−1 12:00 Київ), тож порівняння з фактом чесне.
// Лінія = сімейство моделей: BASE — факторна модель (зараз gbm-v1, далі base-v1.x), EXPRO — AI-аналітик ExPro.
// «Як учора» — та сама година годинника в останню відому добу; з ним порівнюємо кожну лінію.
//
// Визначення індексів (як на сайті ОРЕЄ і в плитках /tg): BASE — середньоарифметична за всі години;
// PEAK — години ОРЕЄ 9–20 (годинник 08:00–19:59); OFFPEAK — решта; середньозважена — Σ(ціна·обсяг)/Σ обсяг.
import { useMemo } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { API_BASE_URL, authFetch } from '@/services/api';
import i18n from '@/i18n';

export type LineKey = 'base' | 'expro';
export const LINE_KEYS: LineKey[] = ['base', 'expro'];

export interface Band { p10: number | null; p50: number | null; p90: number | null }
export interface LineHour { hour: number; clock: number; actual: number | null; volume: number | null; naive: number | null; naiveVolume: number | null; base: Band | null; expro: Band | null }
export interface DayMetrics { mae: number; bias: number; accurate: number; total: number; inCorridor?: number }
export interface Bess { expected?: number; realized?: number; ideal?: number; naiveRealized?: number }
export interface LineRun { id: string; model: string; asof: string; issuedAt: string; retro: boolean; horizon: number }
export interface LinesDay {
  date: string; hasFact: boolean; hours: LineHour[]; lastFact: string | null;
  lines: Record<LineKey, { run: LineRun; metrics: DayMetrics | null; bess: Bess | null } | null>;
  naive: { day: string; metrics: DayMetrics | null; bess?: { realized?: number; ideal?: number } | null };
  tolerance: { rel: number; abs: number };
}
export interface Summary { base: number; peak: number | null; offpeak: number | null; min: number; max: number }
export interface Totals { days: number; hours: number; mae: number; bias: number; accurate: number; inCorridor?: number; corridorWidth?: number | null;
  bess?: { days: number; realized: number; ideal: number };
  /** «як учора» на тих самих добах, що й лінія (для чесного порівняння) */
  naive?: { mae: number; accurate: number; hours: number; bessRealized: number | null } | null }
export interface RangeDay {
  date: string; hasFact: boolean; volume: number | null; weighted: number | null;
  actual: Summary | null; naive: Summary | null; base: Summary | null; expro: Summary | null;
  runs: Record<LineKey, { model: string; asof: string; horizon: number; retro: boolean } | null>;
  metrics: Record<LineKey | 'naive', DayMetrics | null>;
}
export interface RangeResp { from: string; to: string; lastFact: string | null; days: RangeDay[]; totals: Record<LineKey | 'naive', Totals | null> }

const dayCache = new Map<string, LinesDay>();
export async function loadLinesDay(date: string): Promise<LinesDay | null> {
  const hit = dayCache.get(date);
  if (hit) return hit;
  const r = await authFetch(`${API_BASE_URL}/market/price-forecast/lines?date=${date}`);
  if (!r.ok) return null;
  const j = (await r.json()) as LinesDay;
  // кешуємо лише доби з фактом і всіма прогонами в минулому — майбутні оновлюються щодня
  if (j.hasFact && j.date < todayKyiv()) dayCache.set(date, j);
  return j;
}
export async function loadRange(q: { month: string } | { from: string; to: string }): Promise<RangeResp | null> {
  const qs = 'month' in q ? `month=${q.month}` : `from=${q.from}&to=${q.to}`;
  const r = await authFetch(`${API_BASE_URL}/market/price-forecast/range?${qs}`);
  return r.ok ? r.json() : null;
}

export function todayKyiv(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

const loc = () => (i18n.language === 'en' ? 'en-GB' : 'uk-UA');
export const f0 = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString(loc()));
export const f2 = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString(loc(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const pct = (v: number | null | undefined, d = 1) =>
  (v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toLocaleString(loc(), { minimumFractionDigits: d, maximumFractionDigits: d })} %`);
export const dmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');
export const hh = (clock: number) => `${String(clock).padStart(2, '0')}:00`;
/** Відхилення, % = (факт − прогноз) / прогноз — як у плитках /tg. Колір нейтральний: «більше» не означає «краще». */
export const dev = (a: number | null | undefined, f: number | null | undefined) => (a != null && f != null && f !== 0 ? (a - f) / f * 100 : null);

/** Індекси доби з погодинних цін (ключ — годинник Києва, щоб доби переходу часу рахувались правильно). */
export function indices(rows: { clock: number; price: number | null; volume?: number | null }[]) {
  const p = rows.filter((r) => r.price != null) as { clock: number; price: number; volume?: number | null }[];
  if (!p.length || p.length < rows.length) return null;
  const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  let pv = 0, v = 0;
  for (const r of p) if (r.volume != null && r.volume > 0) { pv += r.price * r.volume; v += r.volume; }
  return {
    base: avg(p.map((r) => r.price)),
    weighted: v > 0 ? pv / v : null,
    peak: avg(p.filter((r) => r.clock >= 8 && r.clock <= 19).map((r) => r.price)),
    offpeak: avg(p.filter((r) => r.clock < 8 || r.clock > 19).map((r) => r.price)),
  };
}

/** Кольори ліній — токени brandbook ТЕПЛА, кожна лінія свого «роду», щоб розрізнялись з першого погляду:
 *  Факт — приглушений брендовий червоний --series-3 (як погодили на нараді 28.10: факт червоний, прогноз — ні);
 *  BASE — графіт --text (основна модель, суцільна, межі — сіра заливка);
 *  EXPRO — бірюзовий --ok-2 #1F8A6B (альтернативна лінія, штрихпунктир — як ECMWF у прогнозі генерації);
 *  «Як учора» — --text-dim пунктиром (лише базис порівняння). Яскравий червоний (--accent) не беремо — він для помилок. */
export function useLineColors() {
  const { isDark } = useTheme();
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;
    return {
      fact: v('--series-3', '#A6575B'),
      base: v('--text', '#28323C'),
      expro: v('--ok-2', '#1F8A6B'),
      naive: v('--text-dim', '#9AA0A6'),
      vol: v('--series-2', '#5E656D'),
      grid: v('--line', '#E4E5E7'),
      text: v('--text', '#28323C'),
      muted: v('--text-muted', '#5E656D'),
      dim: v('--text-dim', '#9AA0A6'),
      surface: v('--surface-solid', '#FFFFFF'),
      ok: v('--ok', '#2E7D46'),
      warn: v('--warn', '#C77A1B'),
    };
  }, [isDark]);
}
export const DASH: Record<'expro' | 'naive', string> = { expro: '10 4 2 4', naive: '3 4' };
